/**
 * Prepaid-wallet cost panel for a campaign launch (backend: trigger-preview's
 * `wallet`). Renders nothing for workspaces the wallet doesn't meter.
 *
 * The estimate is an upper bound priced exactly like the per-message hold; the
 * conversation reserve is money campaigns cannot spend (it keeps replies,
 * flows and API sends going).
 */
import React from 'react';
import { Alert, AlertTitle, Box, Stack, Tooltip, Typography } from '@mui/material';
import { AccountBalanceWalletOutlined, InfoOutlined } from '@mui/icons-material';
import { formatInr } from '@/services/wallet.api';

export interface CampaignWalletPreview {
  messages: number;
  needed: number;
  available: number;
  reserve: number;
  spendable: number;
  shortfall: number;
  sufficient: boolean;
  frozen: boolean;
}

export const isWalletShort = (w: CampaignWalletPreview | null | undefined): boolean => Boolean(w && !w.sufficient);

const CampaignWalletEstimate: React.FC<{ wallet: CampaignWalletPreview | null | undefined }> = ({ wallet }) => {
  if (!wallet) return null;
  const reserveNote =
    wallet.reserve > 0 ? ` (${formatInr(wallet.reserve)} is kept aside for customer conversations)` : '';

  if (wallet.frozen) {
    return (
      <Alert severity="error" sx={{ mt: 1.5 }}>
        <AlertTitle>Wallet frozen</AlertTitle>
        This workspace's wallet is frozen, so campaigns can't be sent. Please contact support.
      </Alert>
    );
  }
  if (!wallet.sufficient) {
    return (
      <Alert severity="error" sx={{ mt: 1.5 }}>
        <AlertTitle>Not enough wallet balance</AlertTitle>
        <Typography variant="body2">
          {wallet.messages} message{wallet.messages === 1 ? '' : 's'} need about <b>{formatInr(wallet.needed)}</b>;
          campaigns can use {formatInr(wallet.spendable)}{reserveNote}.
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Top up at least <b>{formatInr(wallet.shortfall)}</b> to send. Nothing is sent or charged until then.
        </Typography>
      </Alert>
    );
  }
  // Affordable: one quiet line — the reserve/billing detail lives in the info tooltip.
  const details = (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="body2" sx={{ mb: 0.75 }}>
        Upper estimate for {wallet.messages} message{wallet.messages === 1 ? '' : 's'}. You are charged only for messages
        WhatsApp bills.
      </Typography>
      <Typography variant="body2">Campaigns can use {formatInr(wallet.spendable)}.</Typography>
      {wallet.reserve > 0 && (
        <Typography variant="body2">{formatInr(wallet.reserve)} is kept aside for customer conversations.</Typography>
      )}
    </Box>
  );
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{ mt: 1.5, px: 1.5, py: 1, borderRadius: 2, border: 1, borderColor: 'divider', bgcolor: 'action.hover' }}
    >
      <AccountBalanceWalletOutlined sx={{ fontSize: 18, color: 'text.secondary' }} />
      <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
        {wallet.needed > 0 ? (
          <>
            Estimated wallet cost{' '}
            <Box component="b" sx={{ color: 'text.primary' }}>
              up to {formatInr(wallet.needed)}
            </Box>
          </>
        ) : (
          'No wallet charge for this send'
        )}
      </Typography>
      <Tooltip title={details} arrow placement="top">
        <InfoOutlined
          tabIndex={0}
          aria-label="Wallet cost details"
          sx={{ fontSize: 18, color: 'text.disabled', cursor: 'help', '&:hover, &:focus': { color: 'text.secondary' } }}
        />
      </Tooltip>
    </Stack>
  );
};

export default CampaignWalletEstimate;
